/**
 * Week Editor
 *
 * Weekly view showing all day patterns with mini previews.
 * Allows editing individual days and copying patterns between days.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import type { DaySchedule, TimeValue, DayType, ScheduleTypeLimits } from './types';
import { DayEditor } from './DayEditor';

/**
 * Toast notification state for copy feedback
 */
interface ToastState {
  message: string;
  visible: boolean;
}

/**
 * Simple toast component for copy feedback
 */
function CopyToast({ message, visible }: ToastState) {
  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 rounded-lg bg-foreground px-4 py-2 text-sm text-background shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-200"
      role="status"
      aria-live="polite"
    >
      {message}
    </div>
  );
}

/**
 * Day display names
 */
const DAY_NAMES: Record<DayType, string> = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
  Weekdays: 'Weekdays',
  Weekends: 'Weekends',
  AllDays: 'All Days',
  AllOtherDays: 'Other',
  Holidays: 'Holidays',
  SummerDesignDay: 'Summer',
  WinterDesignDay: 'Winter',
  CustomDay1: 'Custom 1',
  CustomDay2: 'Custom 2',
};

/**
 * Full day names
 */
const FULL_DAY_NAMES: Record<DayType, string> = {
  Monday: 'Monday',
  Tuesday: 'Tuesday',
  Wednesday: 'Wednesday',
  Thursday: 'Thursday',
  Friday: 'Friday',
  Saturday: 'Saturday',
  Sunday: 'Sunday',
  Weekdays: 'Weekdays',
  Weekends: 'Weekends',
  AllDays: 'All Days',
  AllOtherDays: 'All Other Days',
  Holidays: 'Holidays',
  SummerDesignDay: 'Summer Design Day',
  WinterDesignDay: 'Winter Design Day',
  CustomDay1: 'Custom Day 1',
  CustomDay2: 'Custom Day 2',
};

/**
 * Convert time values to mini bar visualization data
 */
function getMiniBars(values: TimeValue[], minValue: number, maxValue: number): number[] {
  const bars = new Array(24).fill(0) as number[];
  const range = maxValue - minValue || 1;

  if (values.length === 0) return bars;

  // Sort values by time
  const sorted = [...values].sort((a, b) => {
    const aMinutes = a.hour * 60 + a.minute;
    const bMinutes = b.hour * 60 + b.minute;
    return aMinutes - bMinutes;
  });

  // Fill in normalized values (0-1)
  let currentValue = 0;
  let valueIndex = 0;

  for (let hour = 0; hour < 24; hour++) {
    while (valueIndex < sorted.length) {
      const tv = sorted[valueIndex];
      if (!tv) break;
      const tvHour = tv.hour === 24 ? 24 : tv.hour;
      const tvMinutes = tvHour * 60 + tv.minute;
      const hourEnd = (hour + 1) * 60;

      if (tvMinutes <= hourEnd) {
        currentValue = (tv.value - minValue) / range;
        if (tvMinutes <= hour * 60) {
          valueIndex++;
        } else {
          break;
        }
      } else {
        break;
      }
    }
    bars[hour] = Math.max(0, Math.min(1, currentValue));
  }

  return bars;
}

/**
 * Mini bar chart preview for a day
 */
function MiniBarChart({ values, minValue, maxValue }: { values: TimeValue[]; minValue: number; maxValue: number }) {
  const bars = useMemo(() => getMiniBars(values, minValue, maxValue), [values, minValue, maxValue]);

  // Calculate average and peak for summary
  const stats = useMemo(() => {
    if (bars.length === 0) return { avg: 0, peak: 0 };
    const sum = bars.reduce((a, b) => a + b, 0);
    return {
      avg: sum / bars.length,
      peak: Math.max(...bars),
    };
  }, [bars]);

  return (
    <div
      className="flex h-10 items-end gap-px group relative"
      title={`Avg: ${(stats.avg * (maxValue - minValue) + minValue).toFixed(1)}, Peak: ${(stats.peak * (maxValue - minValue) + minValue).toFixed(1)}`}
    >
      {bars.map((height, i) => (
        <div
          key={i}
          className="flex-1 bg-primary/70 dark:bg-primary/60 rounded-t transition-colors group-hover:bg-primary"
          style={{ height: `${height * 100}%`, minHeight: height > 0 ? '2px' : '0' }}
        />
      ))}
      {/* Overlay showing avg line */}
      <div
        className="absolute left-0 right-0 border-t-2 border-dashed border-primary/40 pointer-events-none"
        style={{ bottom: `${stats.avg * 100}%` }}
      />
    </div>
  );
}

/**
 * Individual day row in the week view
 */
function DayRow({
  dayType,
  daySchedule,
  isSelected,
  onClick,
  onCopy,
  minValue,
  maxValue,
}: {
  dayType: DayType;
  daySchedule: DaySchedule | null;
  isSelected: boolean;
  onClick: () => void;
  onCopy: () => void;
  minValue: number;
  maxValue: number;
}) {
  const values = daySchedule?.values || [];
  const hasData = values.length > 0;

  return (
    <div
      className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 ${
        isSelected
          ? 'border-primary bg-primary/10 dark:bg-primary/20'
          : 'border-border hover:border-primary/50 hover:bg-muted'
      }`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      role="option"
      tabIndex={0}
      aria-selected={isSelected}
      aria-label={`${FULL_DAY_NAMES[dayType]}, ${hasData ? 'has schedule data' : 'no data'}`}
    >
      <div className="w-16 text-sm font-medium text-foreground">{DAY_NAMES[dayType]}</div>
      <div className="flex-1">
        {hasData ? (
          <MiniBarChart values={values} minValue={minValue} maxValue={maxValue} />
        ) : (
          <div className="h-10 flex items-center text-xs text-muted-foreground italic">No data</div>
        )}
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onCopy();
        }}
        className="rounded p-2 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        title="Copy to clipboard"
        aria-label={`Copy ${FULL_DAY_NAMES[dayType]} schedule`}
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
        </svg>
      </button>
    </div>
  );
}

interface WeekEditorProps {
  /** Day schedules for the period */
  daySchedules: DaySchedule[];
  /** Callback when day schedules change */
  onChange: (daySchedules: DaySchedule[]) => void;
  /** Schedule type limits */
  typeLimits?: ScheduleTypeLimits | null;
  /** Whether the editor is read-only */
  readOnly?: boolean;
}

/**
 * Week Editor Component
 */
export function WeekEditor({
  daySchedules,
  onChange,
  typeLimits,
  readOnly = false,
}: WeekEditorProps) {
  // Default to Monday selected for better UX
  const [selectedDayType, setSelectedDayType] = useState<DayType | null>('Monday');
  const [clipboard, setClipboard] = useState<TimeValue[] | null>(null);
  const [toast, setToast] = useState<ToastState>({ message: '', visible: false });
  const toastTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const minValue = typeLimits?.lowerLimit ?? 0;
  const maxValue = typeLimits?.upperLimit ?? 1;

  // Clean up toast timer on unmount
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // Show toast helper
  const showToast = useCallback((message: string) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, visible: true });
    toastTimerRef.current = setTimeout(() => setToast((t) => ({ ...t, visible: false })), 2000);
  }, []);

  // Map day schedules to a lookup by day type
  const dayScheduleMap = useMemo(() => {
    const map = new Map<DayType, DaySchedule>();
    for (const ds of daySchedules) {
      for (const dt of ds.dayTypes) {
        map.set(dt, ds);
      }
    }
    return map;
  }, [daySchedules]);

  // Find day schedule for a specific day type
  const getDaySchedule = useCallback(
    (dayType: DayType): DaySchedule | null => {
      // Direct match
      const direct = dayScheduleMap.get(dayType);
      if (direct) return direct;

      // Check aggregate types
      if (
        dayType === 'Monday' ||
        dayType === 'Tuesday' ||
        dayType === 'Wednesday' ||
        dayType === 'Thursday' ||
        dayType === 'Friday'
      ) {
        const weekdays = dayScheduleMap.get('Weekdays');
        if (weekdays) return weekdays;
      }

      if (dayType === 'Saturday' || dayType === 'Sunday') {
        const weekends = dayScheduleMap.get('Weekends');
        if (weekends) return weekends;
      }

      // Check AllDays
      const allDays = dayScheduleMap.get('AllDays');
      if (allDays) return allDays;

      // Check AllOtherDays as fallback
      const allOther = dayScheduleMap.get('AllOtherDays');
      if (allOther) return allOther;

      return null;
    },
    [dayScheduleMap]
  );

  // Get selected day schedule
  const selectedDaySchedule = selectedDayType ? getDaySchedule(selectedDayType) : null;

  // Handle updating values for a specific day type
  const handleUpdateDayValues = useCallback(
    (newValues: TimeValue[]) => {
      if (!selectedDayType || readOnly) return;

      // Find existing day schedule that includes this day type
      const existingIndex = daySchedules.findIndex((ds) =>
        ds.dayTypes.includes(selectedDayType)
      );

      let newDaySchedules: DaySchedule[];

      if (existingIndex >= 0) {
        const existing = daySchedules[existingIndex];
        if (!existing) return;

        // If the existing schedule has multiple day types, we need to split it
        if (existing.dayTypes.length > 1) {
          // Remove this day type from the existing schedule
          const remainingTypes = existing.dayTypes.filter((dt) => dt !== selectedDayType);
          newDaySchedules = [
            ...daySchedules.slice(0, existingIndex),
            { dayTypes: remainingTypes, values: existing.values },
            { dayTypes: [selectedDayType], values: newValues },
            ...daySchedules.slice(existingIndex + 1),
          ];
        } else {
          // Just update the values
          newDaySchedules = daySchedules.map((ds, i) =>
            i === existingIndex ? { ...ds, values: newValues } : ds
          );
        }
      } else {
        // Add new day schedule
        newDaySchedules = [...daySchedules, { dayTypes: [selectedDayType], values: newValues }];
      }

      onChange(newDaySchedules);
    },
    [selectedDayType, daySchedules, onChange, readOnly]
  );

  // Handle copying day values to clipboard
  const handleCopyDay = useCallback((dayType: DayType) => {
    const ds = getDaySchedule(dayType);
    if (ds) {
      setClipboard([...ds.values]);
      showToast(`Copied ${FULL_DAY_NAMES[dayType]} schedule`);
    }
  }, [getDaySchedule, showToast]);

  // Handle pasting clipboard to selected day
  const handlePaste = useCallback(() => {
    if (!clipboard || !selectedDayType || readOnly) return;
    handleUpdateDayValues([...clipboard]);
  }, [clipboard, selectedDayType, handleUpdateDayValues, readOnly]);

  // Handle copying to all weekdays
  const handleCopyToWeekdays = useCallback(() => {
    if (!selectedDayType || readOnly) return;
    const source = getDaySchedule(selectedDayType);
    if (!source) return;

    const weekdayTypes: DayType[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

    // Remove weekday-related schedules and add new consolidated one
    const filteredSchedules = daySchedules.filter(
      (ds) => !ds.dayTypes.some((dt) => weekdayTypes.includes(dt) || dt === 'Weekdays')
    );

    onChange([
      ...filteredSchedules,
      { dayTypes: ['Weekdays'], values: [...source.values] },
    ]);
    showToast('Applied to all weekdays');
  }, [selectedDayType, getDaySchedule, daySchedules, onChange, readOnly, showToast]);

  // Handle copying to weekends
  const handleCopyToWeekends = useCallback(() => {
    if (!selectedDayType || readOnly) return;
    const source = getDaySchedule(selectedDayType);
    if (!source) return;

    const weekendTypes: DayType[] = ['Saturday', 'Sunday'];

    // Remove weekend-related schedules and add new consolidated one
    const filteredSchedules = daySchedules.filter(
      (ds) => !ds.dayTypes.some((dt) => weekendTypes.includes(dt) || dt === 'Weekends')
    );

    onChange([
      ...filteredSchedules,
      { dayTypes: ['Weekends'], values: [...source.values] },
    ]);
    showToast('Applied to weekends');
  }, [selectedDayType, getDaySchedule, daySchedules, onChange, readOnly, showToast]);

  // Days to show in the main section
  const mainDays: DayType[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Design days section
  const designDays: DayType[] = ['SummerDesignDay', 'WinterDesignDay'];

  // Special days section
  const specialDays: DayType[] = ['Holidays'];

  return (
    <div className="flex flex-col gap-4">
      {/* Week grid */}
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-medium text-foreground">Week Pattern</h3>
        <div className="space-y-2" role="listbox" aria-label="Select a day of the week">
          {mainDays.map((day) => (
            <DayRow
              key={day}
              dayType={day}
              daySchedule={getDaySchedule(day)}
              isSelected={selectedDayType === day}
              onClick={() => setSelectedDayType(day)}
              onCopy={() => handleCopyDay(day)}
              minValue={minValue}
              maxValue={maxValue}
            />
          ))}
        </div>
      </div>

      {/* Design days */}
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-medium text-foreground">Design Days</h3>
        <div className="space-y-2" role="listbox" aria-label="Select a design day">
          {designDays.map((day) => (
            <DayRow
              key={day}
              dayType={day}
              daySchedule={getDaySchedule(day)}
              isSelected={selectedDayType === day}
              onClick={() => setSelectedDayType(day)}
              onCopy={() => handleCopyDay(day)}
              minValue={minValue}
              maxValue={maxValue}
            />
          ))}
        </div>
      </div>

      {/* Holidays */}
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-medium text-foreground">Special Days</h3>
        <div className="space-y-2" role="listbox" aria-label="Select a special day">
          {specialDays.map((day) => (
            <DayRow
              key={day}
              dayType={day}
              daySchedule={getDaySchedule(day)}
              isSelected={selectedDayType === day}
              onClick={() => setSelectedDayType(day)}
              onCopy={() => handleCopyDay(day)}
              minValue={minValue}
              maxValue={maxValue}
            />
          ))}
        </div>
      </div>

      {/* Day editor panel */}
      {selectedDayType && (
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-medium text-foreground">
              Edit: {FULL_DAY_NAMES[selectedDayType]}
            </h3>
            {!readOnly && (
              <div className="flex flex-wrap items-center gap-2">
                {clipboard && (
                  <button
                    onClick={handlePaste}
                    className="flex items-center gap-1 rounded border border-border px-3 py-1.5 text-xs text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 2h-4.18C14.4.84 13.3 0 12 0c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm7 18H5V4h2v3h10V4h2v16z" />
                    </svg>
                    Paste
                  </button>
                )}
                {(selectedDayType === 'Monday' ||
                  selectedDayType === 'Tuesday' ||
                  selectedDayType === 'Wednesday' ||
                  selectedDayType === 'Thursday' ||
                  selectedDayType === 'Friday') && (
                  <button
                    onClick={handleCopyToWeekdays}
                    className="rounded border border-border px-3 py-1.5 text-xs text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    Copy to All Weekdays
                  </button>
                )}
                {(selectedDayType === 'Saturday' || selectedDayType === 'Sunday') && (
                  <button
                    onClick={handleCopyToWeekends}
                    className="rounded border border-border px-3 py-1.5 text-xs text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    Copy to Weekends
                  </button>
                )}
              </div>
            )}
          </div>
          <DayEditor
            values={selectedDaySchedule?.values || []}
            dayTypes={[selectedDayType]}
            onChange={handleUpdateDayValues}
            typeLimits={typeLimits ?? null}
            readOnly={readOnly}
          />
        </div>
      )}

      {/* No selection prompt - This shouldn't show now since we default to Monday */}
      {!selectedDayType && (
        <div className="rounded-lg border border-dashed border-border bg-muted/50 p-8 text-center">
          <svg
            className="mx-auto h-12 w-12 text-muted-foreground/50"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01" />
          </svg>
          <p className="mt-2 text-sm text-muted-foreground">Select a day to edit its schedule</p>
        </div>
      )}

      {/* Toast notification */}
      <CopyToast {...toast} />
    </div>
  );
}
